import { useState } from "react";
import { useFcp } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { NcmIcmsPartilhaDto, SalvarNcmIcmsPartilhaRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, DollarSign, Plus, Pencil, Trash2 } from "lucide-react";
import { NcmSearchCombobox } from "@/components/NcmSearchCombobox";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Estados brasileiros ────────────────────────────────────────────────────

const UFS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA",
  "MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN",
  "RO","RR","RS","SC","SE","SP","TO",
];

// ── Formulário vazio ───────────────────────────────────────────────────────

const EMPTY: SalvarNcmIcmsPartilhaRequest = {
  codigoNcm:         "",
  uf:                "",
  aliqInterestadual: null,
  aliqEstadoDestino: null,
  aliqFcp:           null,
  partilhaDestino:   null,
  aliqTotalDestino:  null,
  partilhaRemetente: null,
  diferencial:       null,
};

// ── Helper de campo decimal ────────────────────────────────────────────────

function DecimalInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}
        placeholder="0,00"
      />
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────

export default function FcpPartilha() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarNcmIcmsPartilhaRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);

  const { query, obterPorId, criar, atualizar, excluir } = useFcp(busca || undefined, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarNcmIcmsPartilhaRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const dto: NcmIcmsPartilhaDto = await obterPorId(id);
      setEditId(id);
      setForm({
        codigoNcm:         dto.codigoNcm ?? "",
        uf:                dto.uf ?? "",
        aliqInterestadual: dto.aliqInterestadual,
        aliqEstadoDestino: dto.aliqEstadoDestino,
        aliqFcp:           dto.aliqFcp,
        partilhaDestino:   dto.partilhaDestino,
        aliqTotalDestino:  dto.aliqTotalDestino,
        partilhaRemetente: dto.partilhaRemetente,
        diferencial:       dto.diferencial,
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar registro FCP.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("FCP por NCM atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("FCP por NCM criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar FCP por NCM.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("Registro excluído.");
    } catch {
      toast.error("Erro ao excluir registro.");
    } finally {
      setDeleteId(null);
    }
  };

  const fmt = (v: number | null | undefined) =>
    v != null ? `${v.toFixed(2)}%` : "—";

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" /> FCP por NCM
          </h1>
          <p className="text-muted-foreground">NCM ICMS Partilha — Fundo de Combate à Pobreza</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo</Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por NCM ou UF..."
          className="pl-9"
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código NCM</TableHead>
              <TableHead className="w-[80px]">UF</TableHead>
              <TableHead className="text-right w-[100px]">% FCP</TableHead>
              <TableHead className="text-right w-[140px]">% Interestadual</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum registro encontrado.</TableCell></TableRow>
            ) : itens.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-mono font-semibold">{item.codigoNcm ?? "—"}</TableCell>
                <TableCell className="font-semibold">{item.uf ?? "—"}</TableCell>
                <TableCell className="text-right text-sm">{fmt(item.aliqFcp)}</TableCell>
                <TableCell className="text-right text-sm">{fmt(item.aliqInterestadual)}</TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(item.id)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Editar FCP por NCM" : "Novo FCP por NCM"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>Código NCM</Label>
              <NcmSearchCombobox
                valueCodigo={form.codigoNcm ?? ""}
                onChange={(_codigoInterno, codigo) => set("codigoNcm", codigo)}
                placeholder="Buscar NCM por código ou descrição..."
              />
            </div>
            <div className="space-y-1">
              <Label>UF</Label>
              <Select value={form.uf ?? ""} onValueChange={v => set("uf", v)}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {UFS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DecimalInput label="% FCP" value={form.aliqFcp} onChange={v => set("aliqFcp", v)} />
            <DecimalInput label="% Alíq. Interestadual" value={form.aliqInterestadual} onChange={v => set("aliqInterestadual", v)} />
            <DecimalInput label="% Alíq. Estado Destino" value={form.aliqEstadoDestino} onChange={v => set("aliqEstadoDestino", v)} />
            <DecimalInput label="% Partilha Destino" value={form.partilhaDestino} onChange={v => set("partilhaDestino", v)} />
            <DecimalInput label="% Alíq. Total Destino" value={form.aliqTotalDestino} onChange={v => set("aliqTotalDestino", v)} />
            <DecimalInput label="% Partilha Remetente" value={form.partilhaRemetente} onChange={v => set("partilhaRemetente", v)} />
            <DecimalInput label="% Diferencial" value={form.diferencial} onChange={v => set("diferencial", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir FCP por NCM?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
